import '../credit-application-v2-form/credit-application.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import { Col, Row, Spinner } from 'react-bootstrap';
import { useLocation, useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';

import { useAppContext } from '../../utils/content.util';
import { buildInlineServiceFormSchema } from '../credit-application-v2-form/validationSchema';
import {
  ApplicantInformationForm,
  initialValuesApplicantInformation,
} from '../../components/credit-application/step-1-applicant-information.component';
import {
  ResidentialAddressForm,
  initialValuesResidentialAddress,
} from '../../components/credit-application/step-2-residential-address.component';
import {
  EmploymentInformationForm,
  initialValuesEmploymentInformation,
} from '../../components/credit-application/step-3-employment-information.component';
import {
  CoapplicantInformationForm,
  initialValuesStepCoapplicantInformation,
} from '../../components/credit-application/step-4-coapplicant-information.component';
import {
  SaleDetailsForm,
  initialValuesSaleDetails,
} from '../../components/credit-application/step-7-sale-details.component';
import { ConsentForm, initialValuesConsent } from '../../components/credit-application/step-8-consent.component';
import { createApplication, ValidationError } from '../../services/applications.service';
import { getDealer } from '../../services/dealers.service';
import { Dealer } from '../../models/dealer.model';
import { onError } from '../../utils/error.util';
import { CreateApplicationRequest } from '../../models/application.model';
import { CircularProgressComponent } from '../../components/admin/circular-progress.component';
import {
  CoapplicantResidentialAddressForm,
  initialValuesCoapplicantResidentialAddress,
} from '../../components/credit-application/step-5-coapplicant-residential-address.component';
import {
  CoapplicantEmploymentInformationForm,
  initialValuesCoapplicantEmploymentInformation,
} from '../../components/credit-application/step-6-coapplicant-employment-information.component';
import { InputBox } from '../../components/form-elements/inputbox.component';
import { InputBoxPhone } from '../../components/form-elements/inputbox-phone.component';
import { CompanyConfig } from '../../company.config';
import { isDev } from '../../utils/utils';
import { LeadSource } from '../../models/application.model';
import { AwsCognitoUser } from '../../models/user.model';
import { POSITION_OPTIONS } from '../../models/role.model';

declare global {
  interface Window {
    handleCustomFieldChange?: (name: string, value: string) => void;
  }
}

interface InlineServiceFormProps {
  leadSource: LeadSource;
}

const getAnalystName = (currentUser: AwsCognitoUser | undefined) => {
  if (!currentUser) {
    return '';
  }
  const position = currentUser['custom:position'];
  if (position !== POSITION_OPTIONS.analyst) {
    return '';
  }
  const fullName = `${currentUser.given_name ?? ''} ${currentUser.family_name ?? ''}`.trim();
  return fullName || '';
};

const getCreatedBy = (currentUser: AwsCognitoUser | undefined) => {
  if (!currentUser) {
    return '';
  }
  const fullName = `${currentUser.given_name ?? ''} ${currentUser.family_name ?? ''}`.trim();
  return fullName || '';
};

export default function InlineServiceForm({ leadSource }: InlineServiceFormProps) {
  const { currentUser, isAuthenticated } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const isPhone = location.pathname === '/phone/inline-service-form';
  const isEmbedded = location.pathname.startsWith('/embed');

  const { dealerId, dealerLocation, dealerStock, analyticName } = useParams();

  const [searchParams] = useSearchParams();
  const fname = searchParams.get('fname') ?? '';
  const lname = searchParams.get('lname') ?? '';
  const email = searchParams.get('email') ?? '';
  const vehicleYear = searchParams.get('vyr') ?? '';
  const vehicleMake = searchParams.get('vmk') ?? '';
  const vehicleModel = searchParams.get('vmd') ?? '';
  const queryStock = searchParams.get('stock') ?? '';
  const queryLocation = searchParams.get('location') ?? '';

  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
  const [isCreditAppSubmitting, setIsCreditAppSubmitting] = useState<boolean>(false);
  const pendingSubmitValuesRef = useRef<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [dealer, setDealer] = useState<Dealer | undefined>(undefined);
  const [partnerName, setPartnerName] = useState<string | undefined>(undefined);

  useEffect(() => {
    setPartnerName('');
  }, [leadSource]);

  const [customAttributes, setCustomAttributes] = useState<Record<string, string>>({});

  const validationSchema = useMemo(() => buildInlineServiceFormSchema(isPhone), [isPhone]);

  const initialValues = useMemo(() => {
    const applicant = {
      ...initialValuesApplicantInformation,
      firstName: fname,
      lastName: lname,
      emailAddress: email,
      ...(isAuthenticated && currentUser
        ? {
            createdBy: getCreatedBy(currentUser),
          }
        : {}),
    };

    const residential = { ...initialValuesResidentialAddress };

    const merged: Record<string, unknown> = {
      ...applicant,
      ...residential,
      ...initialValuesEmploymentInformation,
      ...initialValuesStepCoapplicantInformation,
      ...initialValuesCoapplicantResidentialAddress,
      applicantResidentialAddressLine1: residential.residentialAddressLine1,
      applicantResidentialCity: residential.residentialCity,
      applicantResidentialState: residential.residentialState,
      applicantResidentialZipCode: residential.residentialZipCode,
      ...initialValuesCoapplicantEmploymentInformation,
      ...initialValuesSaleDetails,
      vehicleYear: vehicleYear || initialValuesSaleDetails.vehicleYear,
      vehicleMake: vehicleMake || initialValuesSaleDetails.vehicleMake,
      vehicleModel: vehicleModel || initialValuesSaleDetails.vehicleModel,
      ...initialValuesConsent,
      firstName: applicant.firstName,
      lastName: applicant.lastName,
      sellerPhoneNumber: dealer?.phone ?? '',
      sellerContact: '',
      dealerId: dealerId ?? '',
      dealerName: dealer?.companyName ?? '',
      referralSource: dealerId ? 'Dealership' : (initialValuesSaleDetails.referralSource ?? ''),
      customAttributes: {},
    };

    return merged;
  }, [
    fname,
    lname,
    email,
    vehicleYear,
    vehicleMake,
    vehicleModel,
    isAuthenticated,
    currentUser,
    dealer?.phone,
    dealer?.companyName,
    dealerId,
  ]);

  useEffect(() => {
    async function onLoad() {
      if (!dealerId) {
        setIsLoading(false);
        return;
      }
      if (isNaN(Number(dealerId))) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await getDealer(dealerId);
        setDealer(response);
        setPartnerName(response.companyName);
      } catch {
        navigate('/');
      }

      setIsLoading(false);
    }

    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId]);

  useEffect(() => {
    if (dealer?.customThemeContent) {
      window.handleCustomFieldChange = function (name, value) {
        setCustomAttributes((prev) => ({
          ...prev,
          [name]: value,
        }));
      };
    }
  }, [dealer?.customThemeContent]);

  function phoneApplication({ values, touched, errors, handleChange, handleBlur, setFieldValue }: any) {
    return (
      <Row>
        <Col xs={12} className="pb-3">
          <h4>Service Form Phone Submission</h4>
        </Col>

        <Col xs={12} md={6}>
          <InputBox
            disabled={isAuthenticated}
            label="Team Member"
            name="createdBy"
            type="text"
            placeholder="Full Name"
            value={values.createdBy}
            onChange={handleChange}
            onBlur={handleBlur}
            required={true}
            isValid={touched.createdBy && !errors.createdBy}
            isInvalid={touched.createdBy && !!errors.createdBy}
            errorFeedback={errors.createdBy}
          />
        </Col>

        <Col xs={12} md={6}>
          <InputBoxPhone
            label="Phone Number Callback"
            name="phoneNumberCallback"
            value={values.phoneNumberCallback}
            onChange={handleChange}
            onBlur={handleBlur}
            setFieldValue={setFieldValue}
            isValid={touched.phoneNumberCallback && !errors.phoneNumberCallback}
            isInvalid={touched.phoneNumberCallback && !!errors.phoneNumberCallback}
            errorFeedback={errors.phoneNumberCallback}
          />
        </Col>
      </Row>
    );
  }

  function embeddedApplication() {
    return (
      <div id="embedded-application-container">
        <Row className="my-3">
          <Col xs={12}>
            <img
              style={{
                display: 'block',
                width: '150px',
                margin: '15px 20px',
                padding: '0px',
                outline: '0',
                border: '0',
              }}
              alt={`${CompanyConfig.name} Logo`}
              src={CompanyConfig.logoUrl}
            />

            <p
              style={{
                display: 'block',
                fontFamily: 'Montserrat',
                fontSize: '13px',
                lineHeight: '1.5',
                color: '#333',
                padding: '0px 15px',
              }}
            >
              <strong>
                This form is provided by {CompanyConfig.name} ({CompanyConfig.shortName}), a third-party financial
                services provider, in partnership with {partnerName}.
              </strong>
              <br />
              By submitting your information, you acknowledge that it will be sent directly to {CompanyConfig.name} for
              processing. {CompanyConfig.name} will share your information with its affiliated lenders for the purpose
              of evaluating financing options related to your inquiry. Submission of this form does <strong>not</strong>{' '}
              constitute a loan approval, or offer of credit. It is intended only to begin the process of exploring
              available financing. If you have any questions about how your information will be used, please read our{' '}
              <a
                style={{ color: '#162f5e', textDecoration: 'underline', outline: '0', border: '0' }}
                href={`${CompanyConfig.website}/privacy`}
                title={`${CompanyConfig.name} Privacy Policy`}
                target="_blank"
                rel="nofollow noreferrer"
              >
                Privacy Policy
              </a>{' '}
              &amp;{' '}
              <a
                style={{ color: '#162f5e', textDecoration: 'underline', outline: '0', border: '0' }}
                href={`${CompanyConfig.website}/terms-of-use`}
                title={`${CompanyConfig.name} Terms of Use`}
                target="_blank"
                rel="nofollow noreferrer"
              >
                Terms of Use
              </a>
              , or{' '}
              <a
                style={{ color: '#162f5e', textDecoration: 'underline', outline: '0', border: '0' }}
                href={`${CompanyConfig.website}/contact`}
                title={`Contact ${CompanyConfig.name}`}
                target="_blank"
                rel="nofollow noreferrer"
              >
                contact {CompanyConfig.name}
              </a>{' '}
              directly before submitting the form.
            </p>
          </Col>
        </Row>
      </div>
    );
  }

  function customThemeContainer() {
    return dealer?.customThemeContent ? (
      <div id="custom-theme-content" dangerouslySetInnerHTML={{ __html: dealer.customThemeContent }} />
    ) : null;
  }

  function disclaimerContainer(dealerArg: Dealer | undefined) {
    return (
      <div id="dealer_info">
        <div className="container-xl">
          <div className="row">
            <p>
              <strong>
                This form is provided by {CompanyConfig.name} ({CompanyConfig.shortName}), a third-party financial
                services provider{dealerArg?.companyName ? `, in partnership with ${dealerArg?.companyName}` : ''}.
              </strong>
              <br />
              By submitting your information, you acknowledge that it will be sent directly to {
                CompanyConfig.shortName
              }{' '}
              for processing. {CompanyConfig.shortName} will share your information with its affiliated lenders for the
              purpose of evaluating financing options related to your inquiry. Submission of this form does not
              constitute a loan approval, or offer of credit. It is intended only to begin the process of exploring
              available financing. If you have any questions about how your information will be used, please read our{' '}
              <Link
                className="link"
                to={CompanyConfig.privacyUrl}
                title={`${CompanyConfig.name} Privacy Policy`}
                target="_blank"
              >
                Privacy Policy
              </Link>{' '}
              &{' '}
              <Link
                className="link"
                to={CompanyConfig.termsUrl}
                title={`${CompanyConfig.name} Terms of Use`}
                target="_blank"
              >
                Terms of Use
              </Link>
              , or{' '}
              <Link
                className="link"
                to={CompanyConfig.contactUrl}
                title={`Contact ${CompanyConfig.name}`}
                target="_blank"
              >
                contact {CompanyConfig.shortName}
              </Link>{' '}
              directly before submitting the form.
              <br />
              <strong>{CompanyConfig.name}</strong>- {CompanyConfig.address.addressLine1}, {CompanyConfig.address.city},{' '}
              {CompanyConfig.address.state} {CompanyConfig.address.zipCode}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const onVerify = useCallback(
    async (token: string) => {
      if (!isCreditAppSubmitting || !pendingSubmitValuesRef.current) {
        return;
      }
      const formValues = pendingSubmitValuesRef.current;
      try {
        const payload: CreateApplicationRequest = {
          ...(formValues as any),
          haveCoApplicant: (formValues.haveCoApplicant as any) === true,
          dealerId: dealer?.id,
          dealerName: dealer?.companyName || (formValues.dealerName as string),
          dealerLocation: dealerLocation || queryLocation,
          dealerStock: dealerStock || queryStock,
          analyticName,
          token,
          customAttributes,
          leadSource,
          analystName: getAnalystName(currentUser),
        };

        await createApplication(payload);

        if (currentUser) {
          navigate('/service-forms');
        } else if (dealerId) {
          window.location.href = CompanyConfig.dealerFormUrl;
        } else {
          window.location.href = CompanyConfig.formSubmittedUrl;
        }
      } catch (err) {
        setIsCreditAppSubmitting(false);
        pendingSubmitValuesRef.current = null;
        if (err instanceof ValidationError) {
          alert(err.errors.join('\n'));
        } else {
          onError(err);
          alert('Something wrong. Try to start over.');
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCreditAppSubmitting],
  );

  const handleFormSubmit = (values: Record<string, unknown>) => {
    pendingSubmitValuesRef.current = values;
    setIsCreditAppSubmitting(true);
    setRefreshReCaptcha((r) => !r);
  };

  return (
    <>
      {isLoading ? (
        <CircularProgressComponent />
      ) : (
        <div id="body-content">
          {!isEmbedded ? <div id="custom-theme-container">{customThemeContainer()}</div> : null}

          <div id="creditapp">
            {disclaimerContainer(dealer)}

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleFormSubmit}
              enableReinitialize
            >
              {({ handleChange, handleBlur, values, touched, errors, setFieldValue }) => (
                <Form noValidate id="InlineServiceForm">
                  {isPhone ? phoneApplication({ values, touched, errors, handleChange, handleBlur, setFieldValue }) : null}
                  {isEmbedded ? embeddedApplication() : null}

                  <section className="inline-service-form__section border-bottom pb-4 mb-4">
                    <ApplicantInformationForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                  </section>

                  <section className="inline-service-form__section border-bottom pb-4 mb-4">
                    <ResidentialAddressForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                  </section>

                  <section className="inline-service-form__section border-bottom pb-4 mb-4">
                    <EmploymentInformationForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                  </section>

                  <section className="inline-service-form__section border-bottom pb-4 mb-4">
                    <CoapplicantInformationForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                  </section>

                  {values.haveCoApplicant === true ? (
                    <section className="inline-service-form__section border-bottom pb-4 mb-4">
                      <CoapplicantResidentialAddressForm
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        setFieldValue={setFieldValue}
                      />
                      <CoapplicantEmploymentInformationForm
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        setFieldValue={setFieldValue}
                      />
                    </section>
                  ) : null}

                  <section className="inline-service-form__section border-bottom pb-4 mb-4">
                    <SaleDetailsForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                  </section>

                  <section className="inline-service-form__section pb-4 mb-4">
                    <ConsentForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      isCustomTheme={dealer?.customThemeContent}
                      isEmbeddedMazo={false}
                    />

                    <Row className="mt-3">
                      <Col xs={12}>
                        <GoogleReCaptcha
                          onVerify={onVerify}
                          refreshReCaptcha={refreshReCaptcha}
                          action="application__inline_service_form_submit"
                        />
                        <p style={{ fontSize: '16px', fontWeight: 'bold' }}>
                          You&apos;ll receive a confirmation email within 10 minutes of clicking &quot;Submit&quot;. If
                          you don&apos;t see it, please check your spam folder. If it&apos;s still not there, give us a
                          call at {CompanyConfig.phone}, and we&apos;ll be happy to assist you further.
                        </p>
                        <button type="submit" disabled={isCreditAppSubmitting} className="bttn bttn-blue">
                          {!isCreditAppSubmitting ? (
                            'Submit'
                          ) : (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                style={{ marginRight: '5px' }}
                              />
                              Submitting...
                            </>
                          )}
                        </button>
                      </Col>
                    </Row>
                  </section>

                  {isDev ? (
                    <>
                      <div>
                        <p>Values:</p>
                        <code>{JSON.stringify(values, null, 2)}</code>
                      </div>
                      <div>
                        <p>Errors:</p>
                        <code>{JSON.stringify(errors, null, 2)}</code>
                      </div>
                    </>
                  ) : null}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </>
  );
}
